import type { ApolloError } from '@apollo/client/errors';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useUpdateAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useUpdateAssessmentActivityWithLinkedItems';
import { useUpdateWizard } from '@/hooks/mutations/wizard';
import { useGetWizardById } from '@/hooks/queries';

import { useWizardStore } from '../store/useWizardStore';
import type { Risk, Step } from '../types';
import { StepStatus } from '../types';
import { isDefaultTaxonomySteps } from '../utils';
import { useDefaultWizardStepConfiguration } from './useDefaultWizardStepConfiguration';
import { useNavigateToStep } from './useNavigateToStep';

export const useInitiateWizard = (
  risk: Risk,
  isOwnerOrContributor: boolean,
  wizardFeatureEnabled: boolean
) => {
  const {
    setCurrentStep,
    setSteps,
    setRisk,
    setAssessmentId,
    setActivityId,
    setWizardStatus,
    setIsNavigatingFromActivity,
    isNavigatingFromActivity,
    setLoading,
    steps,
  } = useWizardStore();
  const { navigateToStep } = useNavigateToStep();

  const parentUrl = useGetDetailParentPath(risk.riskId);

  const { t } = useTranslation('common', { keyPrefix: 'wizard' });

  const [toolsContent, setToolsContent] = useTools();
  const { user } = useRisksmartUser();
  const { addNotification } = useNotifications();
  const defaultSteps = useDefaultWizardStepConfiguration();

  const initiateSteps = (currentStep: number) => {
    const taxonomySteps = t('steps');
    const steps =
      Array.isArray(taxonomySteps) &&
      taxonomySteps.length > 0 &&
      !isDefaultTaxonomySteps(taxonomySteps)
        ? taxonomySteps
        : defaultSteps;

    return steps.map((step: Step, i) => {
      if (i < currentStep) {
        step.status = StepStatus.Complete;

        return step;
      }
      if (i === currentStep) {
        step.status = StepStatus.InProgress;

        return step;
      }

      step.status = StepStatus.ToDO;

      return step;
    });
  };

  const handleError = (error: ApolloError) => {
    addNotification({
      type: 'error',
      content: <>{error.message}</>,
    });
    setLoading(false);
    setToolsContent(undefined);
  };

  const { updateWizard } = useUpdateWizard();

  const { updateAssessmentActivity } =
    useUpdateAssessmentActivityWithLinkedItems();

  const { data, loading } = useGetWizardById({
    queryArgs: { riskId: risk.riskId },
    shouldSkip: !wizardFeatureEnabled || !isOwnerOrContributor,
  });

  useEffect(() => {
    if (!wizardFeatureEnabled || !isOwnerOrContributor) {
      return;
    }

    if (!loading && toolsContent !== 'wizard' && risk.title) {
      setRisk(risk);
      setCurrentStep(data?.wizard[0]?.CurrentStep ?? 0);
      setAssessmentId(data?.wizard[0]?.AssessmentId ?? '');
      setActivityId(data?.wizard[0]?.ActivityId ?? '');
      setSteps(initiateSteps(data?.wizard[0]?.CurrentStep ?? 0));
      setWizardStatus(data?.wizard[0]?.Status ?? null);
    }
    // `loading` MUST stay in the dep list (COI-839). A risk with no wizard row
    // yet settles to `data === undefined` on the tRPC leg — `createQueryHook`
    // maps a null row to `undefined`, so `data`'s identity never changes and
    // the only observable transition is `loading` true -> false. Without it the
    // store is never hydrated on V3 and every wizard mutation keyed on
    // `risk.riskId` sends '' (the data-layer uuid-validates and 400s). The
    // GraphQL leg returns a fresh `{ wizard: [] }`, which is why this was
    // V3-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, risk.title]);

  useEffect(() => {
    if (!wizardFeatureEnabled || !isOwnerOrContributor) {
      return;
    }

    if (!loading && isNavigatingFromActivity && steps.length > 0) {
      (async () => {
        if (
          !data?.wizard[0]?.Status ||
          !data?.wizard[0]?.ActivityId ||
          !data?.wizard[0]?.AssessmentId
        ) {
          return;
        }

        setLoading(true);
        setToolsContent('wizard');

        if (data?.wizard[0]?.Status === WizardStatus.Planned) {
          try {
            await updateWizard({
              object: {
                RiskId: risk.riskId,
                CurrentStep: 0,
                Status: WizardStatus.InProgress,
              },
            });
          } catch (error) {
            // Mirror the legacy Apollo `onError: handleError` semantics: the
            // mutation error is surfaced but execution still falls through to
            // the assessment-activity update (which reads the pre-existing
            // wizard's ActivityId, not this update's result).
            handleError(error as ApolloError);
          }
          try {
            await updateAssessmentActivity({
              Id: data?.wizard[0]?.ActivityId,
              Status: Assessment_Activity_Status_Enum.Inprogress,
              ActivityType: Assessment_Activity_Type_Enum.Task,
              LinkedItemIds: [risk.riskId],
              ParentId: data?.wizard[0]?.AssessmentId,
              IsWizardAction: true,
              OwnerUserIds: [user!.userId],
              OwnerGroupIds: [],
            });
            navigateToStep(`${parentUrl}/${risk.riskId}`, 0);
            setWizardStatus(WizardStatus.InProgress);
            setLoading(false);
          } catch (error) {
            handleError(error as ApolloError);
          }
        }

        if (data?.wizard[0]?.Status === WizardStatus.InProgress) {
          try {
            await updateAssessmentActivity({
              Id: data?.wizard[0]?.ActivityId,
              Status: Assessment_Activity_Status_Enum.Inprogress,
              ActivityType: Assessment_Activity_Type_Enum.Task,
              LinkedItemIds: [risk.riskId],
              ParentId: data?.wizard[0]?.AssessmentId,
              IsWizardAction: true,
              OwnerUserIds: [user!.userId],
              OwnerGroupIds: [],
            });
            navigateToStep(
              `${parentUrl}/${risk.riskId}`,
              data?.wizard[0]?.CurrentStep
            );
            setLoading(false);
          } catch (error) {
            handleError(error as ApolloError);
          }
        }
      })();

      setIsNavigatingFromActivity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, steps]);

  return;
};
